import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import GradientIcon from 'components/ui/GradientIcon';
import ContactMethodModal from 'components/modals/ContactMethodModal';

const ExpertiseSection = () => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('');

  // Handle "Get Help Now" button click
  const handleGetHelp = (subject) => {
    setSelectedSubject(subject);
    setShowContactModal(true);
  };

  // Handle contact method selection
  const handleContactMethod = (method) => {
    if (method === 'whatsapp') {
      const phoneNumber = '254793863320';
      const message = encodeURIComponent(`Hi! I would like help with ${selectedSubject}. Can we discuss my assignment needs?`);
      window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
    } else if (method === 'discord') {
      // Discord integration for consultations
      const message = encodeURIComponent(`I need help with ${selectedSubject}. Can we set up a consultation?`);
      window.open(`https://discord.gg/your-server?message=${message}`, '_blank');
    }
    setShowContactModal(false);
  };

  // Toggle category expansion
  const toggleCategory = (categoryKey) => {
    setExpandedCategory(expandedCategory === categoryKey ? null : categoryKey);
  };

  const subjectCategories = {
    stem: {
      title: "STEM & Engineering",
      icon: "Calculator",
      gradient: "primary",
      subjects: [
        { name: "Mathematics", experts: 45, icon: "Calculator", gradient: "primary" },
        { name: "Computer Science", experts: 38, icon: "Code", gradient: "blue" },
        { name: "Physics", experts: 32, icon: "Zap", gradient: "violet" },
        { name: "Chemistry", experts: 29, icon: "Flask", gradient: "emerald" },
        { name: "Engineering", experts: 41, icon: "Cog", gradient: "gold" },
        { name: "Statistics", experts: 27, icon: "BarChart", gradient: "ocean" }
      ]
    },
    business: {
      title: "Business & Economics",
      icon: "TrendingUp",
      gradient: "ocean",
      subjects: [
        { name: "Business Administration", experts: 35, icon: "Briefcase", gradient: "primary" },
        { name: "Economics", experts: 28, icon: "DollarSign", gradient: "gold" },
        { name: "Marketing", experts: 31, icon: "Target", gradient: "rose" },
        { name: "Finance", experts: 26, icon: "PieChart", gradient: "emerald" },
        { name: "Management", experts: 33, icon: "Users", gradient: "violet" },
        { name: "Accounting", experts: 24, icon: "Calculator", gradient: "blue" }
      ]
    },
    humanities: {
      title: "Humanities & Social Sciences",
      icon: "BookOpen",
      gradient: "violet",
      subjects: [
        { name: "English Literature", experts: 42, icon: "BookOpen", gradient: "primary" },
        { name: "History", experts: 36, icon: "Clock", gradient: "gold" },
        { name: "Psychology", experts: 39, icon: "Brain", gradient: "violet" },
        { name: "Sociology", experts: 25, icon: "Users", gradient: "ocean" },
        { name: "Philosophy", experts: 22, icon: "Lightbulb", gradient: "sunset" },
        { name: "Political Science", experts: 28, icon: "Flag", gradient: "rose" }
      ]
    },
    health: {
      title: "Health & Life Sciences",
      icon: "Heart",
      gradient: "emerald",
      subjects: [
        { name: "Nursing", experts: 34, icon: "Heart", gradient: "rose" },
        { name: "Biology", experts: 31, icon: "Leaf", gradient: "emerald" },
        { name: "Medicine", experts: 29, icon: "Stethoscope", gradient: "primary" },
        { name: "Public Health", experts: 23, icon: "Shield", gradient: "success" },
        { name: "Pharmacy", experts: 19, icon: "Pill", gradient: "violet" },
        { name: "Nutrition", experts: 21, icon: "Apple", gradient: "gold" }
      ]
    }
  };

  // Calculate total experts per category
  const getCategoryStats = (category) => {
    const totalExperts = category.subjects.reduce((sum, subject) => sum + subject.experts, 0);
    return {
      subjectCount: category.subjects.length,
      totalExperts
    };
  };

  // Filter subjects based on search term
  const getFilteredCategories = () => {
    if (!searchTerm) return subjectCategories;
    
    const filtered = {};
    Object.entries(subjectCategories).forEach(([key, category]) => {
      const filteredSubjects = category.subjects.filter(subject =>
        subject.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (filteredSubjects.length > 0) {
        filtered[key] = { ...category, subjects: filteredSubjects };
      }
    });
    return filtered;
  };

  const filteredCategories = getFilteredCategories();

  return (
    <section className="py-20 bg-background">
      {/* Contact Modal */}
      <ContactMethodModal 
        isOpen={showContactModal} 
        onClose={() => setShowContactModal(false)} 
        onSelectMethod={handleContactMethod}
        title={`Get Help with ${selectedSubject}`}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
            <span className="text-gradient">Expert Coverage</span> Across All Subjects
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
            No matter your field of study or academic level, we have qualified experts 
            ready to help you succeed. Browse our comprehensive subject coverage.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-md mx-auto relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="Search" size={20} className="text-text-muted" />
            </div>
            <input
              type="text"
              placeholder="Search subjects (e.g., Mathematics, Psychology...)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-surface border border-border rounded-xl text-text-primary placeholder-text-muted focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all"
            />
          </div>
        </motion.div>

        {/* Accordion Categories */}
        <div className="mb-16 space-y-4">
          {Object.entries(filteredCategories).map(([categoryKey, category], index) => {
            const stats = getCategoryStats(category);
            const isExpanded = expandedCategory === categoryKey;
            
            return (
              <motion.div
                key={categoryKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`backdrop-blur-xl bg-gradient-to-br from-white/15 to-white/10 border border-white/30 rounded-xl overflow-hidden transition-all duration-300 shadow-lg ${
                  isExpanded 
                    ? 'from-purple-500/25 to-purple-500/15 border-purple-400/40 shadow-xl shadow-purple-500/25' 
                    : 'hover:from-white/20 hover:to-white/15 hover:border-white/40 hover:shadow-white/10'
                }`}
              >
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(categoryKey)}
                  className={`w-full p-6 text-left flex items-center justify-between transition-all duration-300 ${
                    isExpanded 
                      ? 'bg-purple-500/10 hover:bg-purple-500/15' 
                      : 'hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <GradientIcon
                      name={category.icon}
                      size={28}
                      gradient={category.gradient}
                      containerClassName="w-14 h-14 rounded-lg"
                    />
                    <div>
                      <h3 className="text-xl font-bold text-white drop-shadow-sm">
                        {category.title}
                      </h3>
                      <div className="text-white/80 text-sm font-medium">
                        {stats.subjectCount} subjects • {stats.totalExperts} experts
                      </div>
                    </div>
                  </div>
                  
                  <motion.div
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Icon name="ChevronDown" size={24} className="text-white/80" />
                  </motion.div>
                </button>

                {/* Expanded Content */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-white/20"
                    >
                      <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 bg-gradient-to-br from-white/15 to-white/10 backdrop-blur-md border-t border-white/20">
                        {category.subjects.map((subject, subjectIndex) => (
                          <motion.div
                            key={subject.name}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: subjectIndex * 0.05 }}
                            className="backdrop-blur-xl bg-gradient-to-br from-white/25 to-white/15 border border-white/40 rounded-xl p-5 hover:from-white/35 hover:to-white/25 hover:border-white/50 hover:shadow-lg hover:shadow-white/10 transition-all duration-300 transform hover:-translate-y-1"
                          >
                            <div className="flex items-center space-x-3 mb-3">
                              <div className="relative">
                                <GradientIcon
                                  name={subject.icon}
                                  size={22}
                                  gradient={subject.gradient}
                                  containerClassName="w-10 h-10 rounded-lg shadow-md"
                                />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-bold text-white text-base drop-shadow-sm">
                                  {subject.name}
                                </h4>
                                <div className="text-white/80 text-sm font-medium">
                                  {subject.experts} experts available
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleGetHelp(subject.name)}
                              className="w-full py-3 px-4 backdrop-blur-md bg-gradient-to-r from-primary/40 to-primary/30 text-white font-bold hover:from-primary/50 hover:to-primary/40 border border-primary/50 hover:border-primary/60 rounded-lg text-sm shadow-lg hover:shadow-primary/20 transition-all duration-300 transform hover:scale-105"
                            >
                              Get Help Now
                            </button>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>


        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center bg-gradient-to-r from-primary/10 to-secondary/10 p-8 rounded-2xl border border-primary/20"
        >
          <h3 className="text-2xl font-bold text-text-primary mb-4">
            Can't Find Your Subject?
          </h3>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto mb-6">
            We cover over 100+ subjects across all academic disciplines. 
            If you don't see your specific subject listed, just ask us - 
            we likely have an expert ready to help.
          </p>
          <button
            onClick={() => {
              setSelectedSubject('Other Subject');
              setShowContactModal(true);
            }}
            className="btn-primary text-lg px-8 py-4 inline-flex items-center space-x-2"
          >
            <Icon name="Search" size={24} className="text-white" />
            <span>Ask About Your Subject</span>
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default ExpertiseSection;
